import React, { useMemo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const ALIGN_CLASS_MAP = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const JUSTIFY_CLASS_MAP = {
  left: 'justify-start',
  start: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

const ALIGN_CONTENT_CLASS_MAP = {
  start: 'content-start',
  center: 'content-center',
  end: 'content-end',
  between: 'content-between',
  around: 'content-around',
  evenly: 'content-evenly',
  stretch: 'content-stretch',
};

const ALIGN_ITEMS_CLASS_MAP = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  baseline: 'items-baseline',
  stretch: 'items-stretch',
};

const ALIGN_SELF_CLASS_MAP = {
  auto: 'self-auto',
  start: 'self-start',
  center: 'self-center',
  end: 'self-end',
  stretch: 'self-stretch',
  baseline: 'self-baseline',
};

const JUSTIFY_CONTENT_CLASS_MAP = JUSTIFY_CLASS_MAP;

const JUSTIFY_ITEMS_CLASS_MAP = {
  start: 'justify-items-start',
  center: 'justify-items-center',
  end: 'justify-items-end',
  stretch: 'justify-items-stretch',
};

const JUSTIFY_SELF_CLASS_MAP = {
  auto: 'justify-self-auto',
  start: 'justify-self-start',
  center: 'justify-self-center',
  end: 'justify-self-end',
  stretch: 'justify-self-stretch',
};

/**
 * DataTable
 *
 * A flexible, paginated and sortable table component.
 * Features:
 * - Column-based sorting (custom compare or accessor supported)
 * - Optional client-side pagination
 * - Skeleton loading rows and empty state rendering
 * - Highly customizable column layout classes and renderers
 *
 * Props: see default values in the function signature for common options.
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  keyField = 'id',
  itemsPerPage = 25,
  enablePagination = true,
  defaultSort = null,
  sortState = null,
  onSortChange,
  skeletonRowCount = 5,
  emptyState: emptyStateProp,
  getRowProps,
  headerBackground = colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.2),
  containerClassName = '',
  tableClassName = '',
  responsive = true,
  expandable = null,
}) => {
  const [internalSort, setInternalSort] = useState(() => ({
    field: defaultSort?.field || null,
    direction: defaultSort?.direction || 'asc',
  }));

  const activeSortField = sortState?.field ?? internalSort.field;
  const activeSortDirection = sortState?.direction ?? internalSort.direction;

  const [currentPage, setCurrentPage] = useState(0);

  const { t } = useTranslation();
  const paginationStrings = t('common.table.pagination', { returnObjects: true });

  useEffect(() => {
    setCurrentPage(0);
  }, [data, itemsPerPage]);

  const defaultEmptyState = {
    icon: null,
    title: t('common.table.empty.title'),
    message: t('common.table.empty.message'),
    actionLabel: null,
    onAction: null,
  };
  const emptyState = { ...defaultEmptyState, ...(emptyStateProp || {}) };

  /**
   * handleSort(column)
   *
   * Toggle sorting for a column. If the column is not sortable the call is no-op.
   * When a sort is applied it will either update internal state (if parent does
   * not control sorting via `sortState`) and always call `onSortChange` if
   * provided so parent components can react.
   */
  const handleSort = (column) => {
    if (!column?.sortable) return;

    const nextField = column.id;
    let nextDirection = 'asc';

    if (activeSortField === column.id) {
      nextDirection = activeSortDirection === 'asc' ? 'desc' : 'asc';
    }

    if (!sortState) {
      setInternalSort({ field: nextField, direction: nextDirection });
    }

    onSortChange?.({ field: nextField, direction: nextDirection });
  };

  /**
   * sortedData (memoized)
   *
   * Returns a sorted copy of `data` according to the active sort field and
   * direction. Supports a column-level `sortCompare` function, a `sortAccessor`
   * or a simple accessor string/property.
   */
  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const list = [...data];

    if (!activeSortField) return list;

    const column = columns.find((col) => col.id === activeSortField);

    const { sortCompare, sortAccessor, accessor } = column || {};

    if (typeof sortCompare === 'function') {
      return list.sort((a, b) => sortCompare(a, b, activeSortDirection));
    }

    const valueAccessor = typeof sortAccessor === 'function'
      ? sortAccessor
      : typeof accessor === 'function'
        ? accessor
        : typeof accessor === 'string'
          ? (row) => row[accessor]
          : (row) => row[activeSortField];

    return list.sort((a, b) => {
      let aValue = valueAccessor(a);
      let bValue = valueAccessor(b);

      if (aValue instanceof Date) aValue = aValue.getTime();
      if (bValue instanceof Date) bValue = bValue.getTime();

      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return activeSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return activeSortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' })
        : String(bValue).localeCompare(String(aValue), undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [columns, data, activeSortField, activeSortDirection]);

  const shouldPaginate = enablePagination && itemsPerPage > 0;

  const totalItems = sortedData.length;
  const totalPages = shouldPaginate ? Math.max(1, Math.ceil(totalItems / itemsPerPage)) : 1;
  const safePage = shouldPaginate ? Math.min(currentPage, totalPages - 1) : 0;
  const startIndex = shouldPaginate ? safePage * itemsPerPage : 0;
  const endIndex = shouldPaginate ? startIndex + Math.min(itemsPerPage, totalItems - startIndex) : totalItems;

  const pageData = shouldPaginate ? sortedData.slice(startIndex, startIndex + itemsPerPage) : sortedData;

  /**
   * goToPage(page)
   *
   * Safely navigate to a page index when pagination is enabled. Bounds are
   * clamped to the valid page range.
   */
  const goToPage = (page) => {
    if (!shouldPaginate) return;
    const nextPage = Math.min(Math.max(page, 0), totalPages - 1);
    setCurrentPage(nextPage);
  };

  // Convenience pagination handlers
  const handlePrevious = () => goToPage(safePage - 1);
  const handleNext = () => goToPage(safePage + 1);

  /**
   * renderSortIcon(column)
   *
   * Returns the appropriate sort icon for the header: a neutral sort glyph
   * when the column is sortable but not active, or an up/down icon when it's
   * the active sort column.
   */
  const renderSortIcon = (column) => {
    if (!column?.sortable) return null;
    if (activeSortField !== column.id) {
      return (
        <FontAwesomeIcon
          icon={faSort}
          className="text-xs"
          style={{ color: UI_COLORS.TEXT.MUTED }}
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={activeSortDirection === 'asc' ? faSortUp : faSortDown}
        className="text-xs"
        style={{ color: UI_COLORS.TEXT.MUTED }}
      />
    );
  };

  /**
   * renderSkeletonRows()
   *
   * Render a set of placeholder rows used when `loading` is true. Column
   * alignment and class names are kept consistent with the real columns so
   * layout doesn't jump when data arrives.
   */
  const renderSkeletonRows = () => {
    return Array.from({ length: skeletonRowCount }).map((_, index) => (
      <tr
        key={`skeleton-${index}`}
        className="border-t animate-pulse"
        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
      >
        {expandable && <td className="px-3 py-4" style={{ width: 40 }} />}
        {columns.map((column) => {
          const alignClass = ALIGN_CLASS_MAP[column.align || 'left'];
          const justifyKey = column.justifyContent ?? column.justify;
          const justifyClass = justifyKey ? JUSTIFY_CONTENT_CLASS_MAP[justifyKey] : null;
          const alignItemsClass = column.alignItems ? ALIGN_ITEMS_CLASS_MAP[column.alignItems] : null;
          const alignContentClass = column.alignContent ? ALIGN_CONTENT_CLASS_MAP[column.alignContent] : null;
          const alignSelfClass = column.alignSelf ? ALIGN_SELF_CLASS_MAP[column.alignSelf] : null;
          const justifyItemsClass = column.justifyItems ? JUSTIFY_ITEMS_CLASS_MAP[column.justifyItems] : null;
          const justifySelfClass = column.justifySelf ? JUSTIFY_SELF_CLASS_MAP[column.justifySelf] : null;

          const classNames = [
            'px-6',
            'py-4',
            alignClass,
            alignSelfClass,
            justifyItemsClass,
            justifySelfClass,
          ];

          if (justifyClass || alignItemsClass || alignContentClass) {
            classNames.push('flex');
            if (justifyClass) classNames.push(justifyClass);
            if (alignItemsClass) classNames.push(alignItemsClass);
            else classNames.push('items-center');
            if (alignContentClass) classNames.push(alignContentClass);
          }

          return (
            <td key={column.id} className={classNames.filter(Boolean).join(' ')}>
              {column.skeleton || <div className="w-full h-4 bg-gray-300 rounded" />}
            </td>
          );
        })}
      </tr>
    ));
  };

  /**
   * renderEmptyState()
   *
   * Renders a friendly empty state row with optional icon, title, message
   * and an action button provided via `emptyState` prop.
   */
  const renderEmptyState = () => (
    <tr>
      <td colSpan={columns.length + (expandable ? 1 : 0)} className="px-6 py-12 text-center">
        {emptyState.icon && (
          <FontAwesomeIcon
            icon={emptyState.icon}
            className="text-5xl mb-5 opacity-50"
            style={{ color: UI_COLORS.TEXT.MUTED }}
          />
        )}
        <h3 className="text-xl font-semibold mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {emptyState.title}
        </h3>
        {emptyState.message && (
          <p className="text-base mb-4" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {emptyState.message}
          </p>
        )}
        {emptyState.actionLabel && typeof emptyState.onAction === 'function' && (
          <button
            onClick={emptyState.onAction}
            className="px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
              color: UI_COLORS.BUTTONS.PRIMARY.text,
            }}
          >
            {emptyState.actionLabel}
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${containerClassName}`}>
      <div className={responsive ? 'overflow-x-auto' : undefined}>
        <table className={`w-full ${tableClassName}`}>
          <thead style={{ backgroundColor: headerBackground }}>
            <tr>
              {expandable && (
                <th
                  className="px-3 py-4"
                  style={{ width: 40, color: UI_COLORS.TEXT.PRIMARY }}
                  aria-label={t('common.actions.expandRow')}
                />
              )}
              {columns.map((column) => {
                const alignClass = ALIGN_CLASS_MAP[column.align || 'left'];
                const isSortable = !!column.sortable;
                const justifyKey = column.justifyContent ?? column.justify;
                const justifyClass = justifyKey ? JUSTIFY_CONTENT_CLASS_MAP[justifyKey] : null;
                const alignItemsClass = column.alignItems ? ALIGN_ITEMS_CLASS_MAP[column.alignItems] : 'items-center';
                const alignContentClass = column.alignContent ? ALIGN_CONTENT_CLASS_MAP[column.alignContent] : null;
                const alignSelfClass = column.alignSelf ? ALIGN_SELF_CLASS_MAP[column.alignSelf] : null;
                const justifyItemsClass = column.justifyItems ? JUSTIFY_ITEMS_CLASS_MAP[column.justifyItems] : null;
                const justifySelfClass = column.justifySelf ? JUSTIFY_SELF_CLASS_MAP[column.justifySelf] : null;

                const isSorted = activeSortField === column.id;
                const headerLabelContent = column.renderHeader
                  ? column.renderHeader({
                    isSorted,
                    sortDirection: isSorted ? activeSortDirection : null,
                    toggleSort: () => handleSort(column),
                    sortable: isSortable,
                    loading,
                  })
                  : column.label;

                const fallbackJustify = justifyClass
                  ? null
                  : column.align === 'right'
                    ? 'justify-end'
                    : column.align === 'center'
                      ? 'justify-center'
                      : 'justify-start';

                const headerFlexClass = [
                  'flex',
                  'gap-2',
                  justifyClass || fallbackJustify,
                  alignItemsClass,
                  alignContentClass,
                ]
                  .filter(Boolean)
                  .join(' ');

                const thClassName = [
                  'px-6',
                  'py-4',
                  'text-left',
                  'font-semibold',
                  'text-sm',
                  'transition-opacity',
                  isSortable && !loading ? 'cursor-pointer hover:opacity-80' : 'cursor-default',
                  alignClass,
                  column.headerClassName,
                  alignSelfClass,
                  justifyItemsClass,
                  justifySelfClass,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <th
                    key={column.id}
                    onClick={() => isSortable && handleSort(column)}
                    className={thClassName}
                    style={{
                      color: UI_COLORS.TEXT.PRIMARY,
                      width: column.width,
                      minWidth: column.minWidth,
                      ...column.headerStyle,
                    }}
                  >
                    <div className={headerFlexClass}>
                      {headerLabelContent !== undefined && headerLabelContent !== null
                        ? typeof headerLabelContent === 'string' || typeof headerLabelContent === 'number'
                          ? <span>{headerLabelContent}</span>
                          : headerLabelContent
                        : null}
                      {renderSortIcon(column)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? renderSkeletonRows()
              : pageData.length > 0
                ? pageData.flatMap((row, rowIndex) => {
                  const rowKey = row?.[keyField] ?? `${rowIndex}`;
                  const rowAdditional = typeof getRowProps === 'function' ? getRowProps(row, rowIndex) : {};
                  const { className: rowClassName = '', style: rowStyle = {}, ...rowEvents } = rowAdditional || {};
                  const isExpanded = expandable && typeof expandable.isRowExpanded === 'function'
                    ? !!expandable.isRowExpanded(row, rowIndex)
                    : false;
                  const totalColSpan = columns.length + (expandable ? 1 : 0);

                  const dataRow = (
                    <tr
                      key={rowKey}
                      className={`border-t transition-colors ${rowClassName}`}
                      style={{
                        borderColor: UI_COLORS.BORDERS.LIGHT,
                        backgroundColor: 'transparent',
                        ...rowStyle,
                      }}
                      {...rowEvents}
                    >
                      {expandable && (
                        <td className="px-3 py-4 align-middle" style={{ width: 40 }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              expandable.onToggle?.(row, rowIndex);
                            }}
                            className="p-1 rounded hover:bg-gray-100 transition-colors"
                            aria-label={isExpanded ? t('common.actions.collapseRow') : t('common.actions.expandRow')}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                              className="text-xs"
                              style={{ color: UI_COLORS.TEXT.MUTED }}
                            />
                          </button>
                        </td>
                      )}
                      {columns.map((column) => {
                        const alignClass = ALIGN_CLASS_MAP[column.align || 'left'];
                        const justifyKey = column.justifyContent ?? column.justify;
                        const justifyClass = justifyKey ? JUSTIFY_CONTENT_CLASS_MAP[justifyKey] : null;
                        const alignItemsClass = column.alignItems ? ALIGN_ITEMS_CLASS_MAP[column.alignItems] : null;
                        const alignContentClass = column.alignContent ? ALIGN_CONTENT_CLASS_MAP[column.alignContent] : null;
                        const alignSelfClass = column.alignSelf ? ALIGN_SELF_CLASS_MAP[column.alignSelf] : null;
                        const justifyItemsClass = column.justifyItems ? JUSTIFY_ITEMS_CLASS_MAP[column.justifyItems] : null;
                        const justifySelfClass = column.justifySelf ? JUSTIFY_SELF_CLASS_MAP[column.justifySelf] : null;

                        const classNames = [
                          'px-6',
                          'py-4',
                          alignClass,
                          column.cellClassName,
                          alignSelfClass,
                          justifyItemsClass,
                          justifySelfClass,
                        ];

                        if (justifyClass || alignItemsClass || alignContentClass) {
                          classNames.push('flex');
                          if (justifyClass) classNames.push(justifyClass);
                          if (alignItemsClass) classNames.push(alignItemsClass);
                          else classNames.push('items-center');
                          if (alignContentClass) classNames.push(alignContentClass);
                        }

                        const CellContent = column.renderCell ? column.renderCell(row, rowIndex) : row[column.id];
                        return (
                          <td
                            key={`${rowKey}-${column.id}`}
                            className={classNames.filter(Boolean).join(' ')}
                            style={column.cellStyle}
                          >
                            {CellContent}
                          </td>
                        );
                      })}
                    </tr>
                  );

                  if (isExpanded && expandable && typeof expandable.renderExpanded === 'function') {
                    const expandedRow = (
                      <tr
                        key={`${rowKey}-expanded`}
                        style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.06) }}
                      >
                        <td colSpan={totalColSpan} className="px-4 py-4">
                          {expandable.renderExpanded(row, rowIndex)}
                        </td>
                      </tr>
                    );
                    return [dataRow, expandedRow];
                  }
                  return [dataRow];
                })
                : renderEmptyState()}
          </tbody>
        </table>
      </div>

      {shouldPaginate && totalItems > 0 && (
        <div
          className="flex flex-col gap-3 border-t px-6 py-4 md:flex-row md:items-center md:justify-between"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {`${paginationStrings.showing} ${totalItems === 0 ? 0 : startIndex + 1}-${endIndex} ${paginationStrings.of} ${totalItems} ${paginationStrings.entries}`}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrevious}
              disabled={safePage === 0}
              className="px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
              }}
            >
              {paginationStrings.previous}
            </button>
            <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {`${paginationStrings.page} ${safePage + 1} ${paginationStrings.of} ${totalPages}`}
            </div>
            <button
              onClick={handleNext}
              disabled={safePage >= totalPages - 1}
              className="px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
              }}
            >
              {paginationStrings.next}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
